import React, { useMemo } from "react";
import { X } from "lucide-react";

import InlineSpinner from "components/Graphs/InlineSpinner";
import SearchableCombobox from "components/Graphs/SearchableCombobox";
import type { OrganizationMember } from "server/devplane/organizations/api";

export type SshAccessUserScope = "none" | "everyone" | "some";

export const getSshAccessUserScope = (
  accessUserIds: string[],
  currentUserId: string | undefined,
  teamMembers: OrganizationMember[],
): SshAccessUserScope => {
  if (accessUserIds.length === 1 && accessUserIds[0] === currentUserId) {
    return "none";
  }

  if (
    teamMembers.length > 0 &&
    teamMembers.every((member) => accessUserIds.includes(member.id))
  ) {
    return "everyone";
  }

  return "some";
};

export const SshAccessScopePill: React.FC<{
  selected: boolean;
  label: string;
  onClick: () => void;
}> = ({ selected, label, onClick }) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    onClick={onClick}
    className={`cursor-pointer whitespace-nowrap rounded-full border px-[11px] py-[7px] text-sm ${
      selected
        ? "border-white/[0.36] bg-white/[0.08] font-semibold text-zinc-200"
        : "border-zinc-800 bg-transparent font-medium text-zinc-400"
    }`}
  >
    {label}
  </button>
);

export const SshAccessScopeNote: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div className="mt-2 text-sm leading-normal text-zinc-400">{children}</div>
);

export const SshAccessUserGrantSelector: React.FC<{
  members: OrganizationMember[];
  selectedUserIds: string[];
  pendingUserIds: string[];
  hasMemberData: boolean;
  onSelect: (userId: string) => void;
  onRemoveSelected: (userId: string) => void;
}> = ({
  members,
  selectedUserIds,
  pendingUserIds,
  hasMemberData,
  onSelect,
  onRemoveSelected,
}) => {
  const selectedUserIdSet = useMemo(
    () => new Set(selectedUserIds),
    [selectedUserIds],
  );
  const pendingUserIdSet = useMemo(
    () => new Set(pendingUserIds),
    [pendingUserIds],
  );
  const memberById = useMemo(
    () => new Map(members.map((member) => [member.id, member])),
    [members],
  );
  const allMembersSelected =
    hasMemberData &&
    members.length > 0 &&
    members.every(
      (member) =>
        selectedUserIdSet.has(member.id) || pendingUserIdSet.has(member.id),
    );
  const availableMembers = members
    .filter(
      (member) =>
        !selectedUserIdSet.has(member.id) && !pendingUserIdSet.has(member.id),
    )
    .sort((first, second) => first.name.localeCompare(second.name));
  const displayedUsers = [
    ...pendingUserIds.map((userId) => ({ userId, status: "pending" })),
    ...selectedUserIds
      .filter((userId) => !pendingUserIdSet.has(userId))
      .map((userId) => ({ userId, status: "selected" })),
  ].sort((first, second) =>
    (memberById.get(first.userId)?.name ?? first.userId).localeCompare(
      memberById.get(second.userId)?.name ?? second.userId,
    ),
  );

  return (
    <div>
      <SearchableCombobox
        id="managed-device-ssh-user-search"
        ariaLabel="Search organization users to grant SSH access"
        options={availableMembers.map((member) => ({
          value: member.id,
          label: member.name,
          description: member.email,
        }))}
        placeholder={
          allMembersSelected
            ? "All organization members are selected"
            : "Search people in your organization..."
        }
        disabled={allMembersSelected}
        emptyMessage="No organization users match your search."
        onSelect={onSelect}
      />
      {hasMemberData && displayedUsers.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-1.5" aria-live="polite">
          {displayedUsers.map(({ userId, status }) => {
            const member = memberById.get(userId);
            return (
              <span
                key={userId}
                className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-highlight bg-transparent px-[7px] py-0.5 text-sm font-semibold text-highlight"
              >
                {status === "pending" ? (
                  <InlineSpinner className="h-3.5 w-3.5" />
                ) : null}
                <span>{member?.name ?? userId}</span>
                {status === "selected" ? (
                  <button
                    type="button"
                    aria-label={`Remove ${member?.name ?? userId}`}
                    onClick={() => onRemoveSelected(userId)}
                    className="inline-flex h-4 w-4 cursor-pointer items-center justify-center rounded-full border-0 bg-highlight/15 p-0 text-sm leading-none text-inherit hover:bg-highlight/30"
                  >
                    <X
                      aria-hidden="true"
                      className="h-3 w-3"
                      strokeWidth={2.5}
                    />
                  </button>
                ) : null}
              </span>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};
